import { ErrorMessage, Form, Formik } from "formik";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useRef, useState } from "react";
import { operatorListColumn } from "../../../appconfig/DataTableColumns";
import {
  convertDateFrom,
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
  statusDropdownItems,
  getUTCStartOfDay,
  getUTCEndOfDay
} from "../../../appconfig/Settings";
import AppBreadcrumb from "../../../components/CustomeBreadcrumb/AppBreadcrumb";
import {
  ADD_OPERATOR,
  CHANGE_OPERATOR_STATUS,
  EDIT_OPERATOR,
  GET_EQUIPMENT_LIST,
  GET_OPERATOR_LIST,
  SUBMIT_MONEY_RECIEVE,
} from "../../../config/ApiConstant";
import {
  moneyReceiveValidationSchema,
  operatorValidationSchema,
} from "../../../config/Validate";
import {
  getAllDetails,
  postAllData,
  updateData,
} from "../../../service/ProductService";
import { Dropdown } from "primereact/dropdown";
import { NavLink } from "react-router-dom";
import { Avatar } from "primereact/avatar";
import { FilterMatchMode } from "primereact/api";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";
import { Calendar } from "primereact/calendar";

// interface
import { OperatorType } from "../../../appconfig/interfaces";
import { Tag } from "primereact/tag";
import { OperatorTab } from "../OperatorsTab";
import { MultiSelect } from "primereact/multiselect";
import { globalFieldTemplate } from "../../../appconfig/TemplateSetting";

const OperatorList = () => {
  document.title = "Operators | Agriown";

  //Set Toast/ Filter Properties
  const toast = useRef<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>("");
  const [statusFilterValue, setStatusFilterValue] = useState<any>(null);

  // formik ref
  const formikRef = useRef<any>(null);
  const moneyFormikRef = useRef<any>(null);

  //operator list state
  const [operatorList, setOperatorList] = useState<any>([]);

  //Add operator Dialog or isedit or not or selected operator set in state
  const [addOperatorDialog, setOperatorDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperator] = useState<any>(null);
  const [confirmStatusDialog, setConfirmStatusDialog] =
    useState<boolean>(false);
  const [statusChangeData, setStatusChangeData] = useState<{
    id: number;
    newStatus: boolean;
  } | null>(null);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // image preview
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [oldPhoto, setOldPhoto] = useState<string | null>(null);

  // set equipment state
  const [equipmentList, setEquipmentList] = useState<any[]>([]);

  // Money Receive dialog state
  const [moneyDialog, setMoneyDialog] = useState<boolean>(false);

  // Date Object
  const [dates, setDates] = useState<string | Date | Date[] | any | null>(null);

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    mobile: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type_of_equipment: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // intial data for formik add operator
  const formInitialValues: OperatorType = {
    name: isEditMode && selectedOperator ? selectedOperator.name : "",
    profile_img:
      isEditMode && selectedOperator ? selectedOperator?.profile_img : "",
    email: isEditMode && selectedOperator ? selectedOperator.email : "",
    mobile: isEditMode && selectedOperator ? selectedOperator.mobile : "",
    type_of_equipment:
      isEditMode && selectedOperator?.type_of_equipment
        ? selectedOperator.type_of_equipment
            .split(",")
            .map((item: string) => item.trim())
        : [],
  };

  // useefect get operator list api
  useEffect(() => {
    if (dates && dates[0] && dates[1]) {
      getOperatorList(dates[0], dates[1]);
    } else if (!dates || (!dates[0] && !dates[1])) {
      getOperatorList();
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [dates]);

  // get operator list api
  const getOperatorList = async (startDate?: Date, endDate?: Date) => {
    try {
      let queryUrl = GET_OPERATOR_LIST;
      if (startDate && endDate) {
        // taking start & end date and converting into formated date
        const start_date = getUTCStartOfDay(startDate);
        const end_date = getUTCEndOfDay(endDate);
        queryUrl += `?start_date=${encodeURIComponent(
          start_date
        )}&end_date=${encodeURIComponent(end_date)}`;
      }

      const response = await getAllDetails(queryUrl);

      if (response) {
        setOperatorList(response);
        setPageLoad(true);
      } else {
        setOperatorList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  //Fetch Equipment list for dropdown
  const getEquipmentList = async () => {
    try {
      const response = await getAllDetails(
        GET_EQUIPMENT_LIST + "?type=dropdown"
      );
      if (response) {
        setEquipmentList(response);
      } else {
        setEquipmentList([]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // onsubmit api call for add edit operator
  const onSubmit = async (values: any, { resetForm }: any) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("mobile", values.mobile);
      if (values.profile_img instanceof File) {
        formData.append("profile_img", values.profile_img);
      }

      formData.append("type_of_equipment", values.type_of_equipment.join(","));

      const response = isEditMode
        ? await updateData(EDIT_OPERATOR, selectedOperator.id, formData)
        : await postAllData(ADD_OPERATOR, formData);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setImagePreview(null);
        setSelectedOperator(null);
        setIsEditMode(false);
        setOperatorDialog(false);
        getOperatorList(dates?.[0], dates?.[1]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // global search or add operator button
  const leftToolbarTemplate = () => {
    return (
      <>
        <span className="mr-0 md:mr-3 search-bar">
          <i className="pi pi-search"></i>
          <InputText
            type="search"
            className="search-input"
            value={globalFilter}
            onInput={(e) => setGlobalFilter(e.currentTarget.value)}
            placeholder="Search..."
          />
        </span>
      </>
    );
  };

  //right toolbar
  const rightToolbarTemplate = () => {
    return (
      <>
        <Calendar
          value={dates}
          className="mr-2 md:mr-3 order-cle"
          dateFormat="dd/mm/yy"
          onChange={(e) => setDates(e.value)}
          selectionMode="range"
          showIcon
          readOnlyInput
        />
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:mr-2 p-button-outlined p-button-primary"
        ></Button>
        <Button
          label="Add Operator"
          icon="pi pi-plus"
          className="ml-2 p-button toolbar-button"
          onClick={async () => {
            await getEquipmentList();
            setOperatorDialog(true);
          }}
        />
      </>
    );
  };

  // close dialog
  const hideAddOperatorDialog = () => {
    setOperatorDialog(false);
    setIsEditMode(false);
    setSelectedOperator(null);
    setImagePreview(null);
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setStatusFilterValue(null);
    setDates(null);
  };

  // on edit handle
  const onEditHandle = async (rowData: any) => {
    setSelectedOperator(rowData);
    setIsEditMode(true);
    setOldPhoto(rowData?.profile_img);
    setImagePreview(null);
    await getEquipmentList();
    setOperatorDialog(true);
  };

  //Change action Template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        className="mr-2 p-button-secondary"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() => onEditHandle(rowData)}
      />
      <Button
        icon="pi pi-money-bill"
        className="mr-2 p-button-secondary"
        tooltip="Money Recieve"
        tooltipOptions={{ position: "top" }}
        onClick={() => {
          setMoneyDialog(true);
          setSelectedOperator(rowData);
        }}
      />
    </div>
  );

  // modal footer content
  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={hideAddOperatorDialog}
        className="p-button-secondary"
      />
      <Button
        label={isEditMode ? "Update" : "Save"}
        icon="pi pi-check"
        type="button"
        loading={loading}
        disabled={loading}
        onClick={() => formikRef.current?.handleSubmit()}
        autoFocus
      />
    </div>
  );

  // Handle confirmed status change
  const handleConfirmStatusChange = () => {
    if (statusChangeData) {
      changeOperatorStatus(statusChangeData.id, statusChangeData.newStatus);
      hideStatusDialog();
    }
  };

  // change operator status api call
  const changeOperatorStatus = async (
    operatorId: number,
    newStatus: boolean
  ) => {
    try {
      const payload = {
        status: newStatus,
      };
      const response = await updateData(
        CHANGE_OPERATOR_STATUS,
        operatorId,
        payload
      );

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        getOperatorList(dates?.[0], dates?.[1]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  const onMoneyDataSubmit = async (values: any, { resetForm }: any) => {
    try {
      setLoading(true);
      const payload = {
        operator_id: selectedOperator.id,
        received_amount: Number(values.received_amount),
      };
      const response = await postAllData(SUBMIT_MONEY_RECIEVE, payload);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setMoneyDialog(false);
        getOperatorList(dates?.[0], dates?.[1]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  //status Template
  const statusTemplate = (rowData: any) => {
    let statusClass =
      rowData.status === true
        ? "status status-active"
        : "status status-inactive";

    return (
      <div className="flex flex-column align-items-center">
        <InputSwitch
          checked={rowData.status}
          className="mb-2 mr-2"
          onChange={(e) => {
            confirmChangeStatus(rowData.id, e.value);
          }}
        />
        <span className={statusClass}>
          {rowData.status === true ? "Active" : "In Active"}
        </span>
      </div>
    );
  };

  // open confirm status change dialog
  const confirmChangeStatus = (id: number, newStatus: boolean) => {
    setStatusChangeData({ id, newStatus });
    setConfirmStatusDialog(true);
  };

  // Hide confirm dialog
  const hideStatusDialog = () => {
    setConfirmStatusDialog(false);
    setStatusChangeData(null);
  };

  //search by type Filter Template
  const searchByType = (options: any) => {
    return (
      <Dropdown
        filter
        value={statusFilterValue}
        options={statusDropdownItems}
        onChange={(e) => {
          setStatusFilterValue(e.value);
          options.filterCallback(e.value ? e.value.code : null, options.index);
        }}
        optionLabel="name"
        placeholder="Search By Status"
        className="order-drp"
        showClear
      />
    );
  };

  // Body template for type_of_equipment
  const typeOfEquipmentBodyTemplate = (rowData: any) => {
    const values = !window.cn(rowData.type_of_equipment)
      ? rowData.type_of_equipment.split(",").map((item: any) => item.trim())
      : [];

    return (
      <div className="flex justify-content-center flex-wrap gap-1">
        {!window.cn(values)
          ? values.map((val: any, index: any) => (
              <Tag key={index} value={val} severity="warning" />
            ))
          : "-"}
      </div>
    );
  };

  //   name template
  const nameTemplate = (rowData: any) => {
    return (
      <>
        <NavLink to={`/operator/detail/${btoa(rowData?.id)}`}>
          <div className="flex avatar-div align-items-center">
            {!window.cn(rowData?.profile_img) ? (
              <Avatar image={rowData?.profile_img} shape="circle" />
            ) : (
              <Avatar
                image={
                  "https://dummyimage.com/100x100/e0e0e0/363636.jpg&text=No+image"
                }
                shape="circle"
              />
            )}
            <div className="ml-2 opt-name">{rowData.name}</div>
          </div>
        </NavLink>
      </>
    );
  };

  return (
    <>
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toast ref={toast} />
            <OperatorTab index={0} />
            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {/* Datatable */}
            {pageLoad ? (
              <>
                <DataTable
                  value={operatorList}
                  showGridlines
                  stripedRows
                  globalFilter={globalFilter || null}
                  rows={defaultRowOptions}
                  paginator={operatorList.length >= 10 && true}
                  rowsPerPageOptions={defaultPageRowOptions}
                  className="datatable-responsive p-datatble-sm"
                  paginatorTemplate={paginatorLinks}
                  currentPageReportTemplate={showingEntries}
                  emptyMessage="No Data Found"
                  filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                  onFilter={(e: any) => setFilters(e.filters)}
                  removableSort
                >
                  {operatorListColumn.map((col, i) => {
                    if (col.field === "action") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={actionBodyTemplate}
                        />
                      );
                    } else if (col.field === "name") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          filter
                          body={nameTemplate}
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "email") {
                      return (
                        <Column
                          key={col.field}
                          sortable
                          field={col.field}
                          body={globalFieldTemplate}
                          filter
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "type_of_equipment") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={typeOfEquipmentBodyTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "mobile") {
                      return (
                        <Column
                          key={col.field}
                          sortable
                          field={col.field}
                          body={globalFieldTemplate}
                          filter
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "status") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={statusTemplate}
                          filter
                          filterElement={searchByType}
                        />
                      );
                    } else {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                        />
                      );
                    }
                  })}
                </DataTable>
              </>
            ) : (
              <>
                {/* Skeleton Data table */}
                <DataTable value={Skeletonitems}>
                  {operatorListColumn.map((col, i) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={SkeletonbodyTemplate}
                    />
                  ))}
                </DataTable>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Money Receive Dialog */}
      <Dialog
        visible={moneyDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "450px" }}
        header={`Money Receive - ${selectedOperator?.name || ""}`}
        draggable={false}
        modal
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              type="button"
              className="p-button-secondary"
              onClick={() => setMoneyDialog(false)}
            />
            <Button
              label="Submit"
              icon="pi pi-check"
              type="submit"
              loading={loading}
              disabled={loading}
              onClick={() => moneyFormikRef.current?.handleSubmit()}
              autoFocus
            />
          </>
        }
        onHide={() => setMoneyDialog(false)}
      >
        <Formik
          enableReinitialize
          innerRef={moneyFormikRef}
          initialValues={{ received_amount: "" }}
          validationSchema={moneyReceiveValidationSchema}
          onSubmit={onMoneyDataSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <Form onSubmit={handleSubmit} className="p-fluid">
              <div className="field col-12">
                <label htmlFor="received_amount">Received Amount *</label>
                <InputText
                  id="received_amount"
                  name="received_amount"
                  value={values.received_amount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  keyfilter="num"
                  className={
                    touched.received_amount && errors.received_amount
                      ? "p-invalid"
                      : ""
                  }
                />
                <small className="p-error">
                  <ErrorMessage name="received_amount" />
                </small>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Operator status confirm */}
      <Dialog
        visible={confirmStatusDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "450px" }}
        draggable={false}
        header="Change Operator Status"
        modal
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={hideStatusDialog}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleConfirmStatusChange}
              loading={loading}
            />
          </>
        }
        onHide={hideStatusDialog}
      >
        <div className="flex align-items-center justify-content-start">
          <i
            className="mr-3 pi pi-exclamation-triangle delete-triangle"
            style={{ fontSize: "2rem" }}
          />
          <span>
            Are you sure you want to set this operator as{" "}
            <b>{statusChangeData?.newStatus ? "Active" : "Inactive"}</b>?
          </span>
        </div>
      </Dialog>

      {/* Add/edit operator Dialog start*/}
      <Dialog
        visible={addOperatorDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "700px" }}
        header={isEditMode ? "Edit Operator" : "Add Operator"}
        draggable={false}
        modal
        onHide={hideAddOperatorDialog}
        footer={footerContent}
      >
        <Formik
          enableReinitialize
          innerRef={formikRef}
          initialValues={formInitialValues}
          validationSchema={operatorValidationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            handleBlur,
            handleSubmit,
          }) => (
            <Form onSubmit={handleSubmit} className="p-fluid">
              <div className="grid p-fluid formgrid">
                {/*  name field */}
                <div className="field col-12 md:col-6">
                  <label htmlFor="name">Name *</label>
                  <InputText
                    id="name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={touched.name && errors.name ? "p-invalid" : ""}
                  />
                  <small className="p-error">
                    <ErrorMessage name="name" />
                  </small>
                </div>

                {/* email field */}
                <div className="field col-12 md:col-6">
                  <label htmlFor="email">Email *</label>
                  <InputText
                    id="email"
                    name="email"
                    onBlur={handleBlur}
                    value={values.email}
                    onChange={handleChange}
                    className={touched.email && errors.email ? "p-invalid" : ""}
                  />
                  <small className="p-error">
                    <ErrorMessage name="email" />
                  </small>
                </div>

                {/* mobile number field */}
                <div className="field col-12 md:col-6">
                  <label htmlFor="mobile">Phone *</label>
                  <InputText
                    keyfilter="pint"
                    maxLength={10}
                    id="mobile"
                    name="mobile"
                    onBlur={handleBlur}
                    value={values.mobile}
                    onChange={handleChange}
                    className={
                      touched.mobile && errors.mobile ? "p-invalid" : ""
                    }
                  />
                  <small className="p-error">
                    <ErrorMessage name="mobile" />
                  </small>
                </div>

                {/*Equipment field */}
                <div className="field col-12 md:col-6">
                  <label htmlFor="type_of_equipment">
                    Type of Equipments *
                  </label>
                  <MultiSelect
                    id="type_of_equipment"
                    name="type_of_equipment"
                    value={values.type_of_equipment}
                    options={equipmentList.map((eq) => ({
                      label: eq.equipment_name,
                      value: eq.equipment_name,
                    }))}
                    onChange={(e) =>
                      setFieldValue("type_of_equipment", e.value)
                    }
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select Equipment"
                    display="chip"
                    className={
                      touched.type_of_equipment && errors.type_of_equipment
                        ? "p-invalid"
                        : ""
                    }
                  />
                  <small className="p-error">
                    <ErrorMessage name="type_of_equipment" />
                  </small>
                </div>

                {/* operator image feild */}
                <div className="flex field align-items-start md:gap-2 justify-content-center md:justify-content-between col-12">
                  <div className="grid block col-12 md:col-6">
                    <label htmlFor="profile_img">Operator Image</label>
                    <div className="upload-box">
                      <i
                        className="pi pi-upload"
                        style={{ fontSize: "1.2rem", marginRight: "0.5rem" }}
                      ></i>
                      <span>
                        {isEditMode ? "Change Image" : "Upload Image"}
                      </span>
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        className="file-input"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setFieldValue("profile_img", file);
                            setImagePreview(URL.createObjectURL(file));
                            if (isEditMode) setOldPhoto(null);
                          }
                        }}
                      />
                    </div>
                    <small className="p-error">
                      <ErrorMessage name="profile_img" />
                    </small>
                  </div>
                  <div className="grid block col-12 md:col-6">
                    {/* Old photo in edit mode */}
                    {isEditMode && oldPhoto && !imagePreview && (
                      <>
                        <label>Current Image</label>
                        <div className="file-preview">
                          <div className="file-info">
                            <a
                              href={oldPhoto}
                              target="_blank"
                              style={{ display: "block" }}
                              rel="noopener noreferrer"
                            >
                              {oldPhoto.split("/").pop()}
                            </a>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Preview new uploaded file */}
                    {imagePreview && (
                      <div className="file-preview">
                        <div className="file-info">
                          <a
                            href={imagePreview}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "block" }}
                          >
                            {values.profile_img instanceof File
                              ? values.profile_img.name
                              : ""}
                          </a>
                          <span className="file-details">
                            {values.profile_img instanceof File
                              ? `${values.profile_img.type} ♦ ${(
                                  values.profile_img.size / 1024
                                ).toFixed(2)} KB`
                              : ""}
                          </span>
                        </div>
                        <i
                          className="pi pi-trash file-delete"
                          onClick={() => {
                            setFieldValue("profile_img", "");
                            setImagePreview(null);
                          }}
                          title="Delete"
                        ></i>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default OperatorList;
